import { useAuthStore } from '@/store/authStore';
import {
  canAccess,
  canWrite,
  hasPermission,
  getAccessibleRoutes,
  type RouteKey,
  type PermissionLevel,
  type AdminRole,
} from '@/types/rbac';

/**
 * Hook that provides permission checks based on the current user's role.
 */
export function usePermissions() {
  const role = useAuthStore((s) => s.user?.role) as AdminRole | undefined;

  return {
    role: role ?? null,

    /** Can the user access this route at all? */
    canAccess: (route: RouteKey) => (role ? canAccess(role, route) : false),

    /** Can the user write (create/edit/delete) on this route? */
    canWrite: (route: RouteKey) => (role ? canWrite(role, route) : false),

    /** Check arbitrary permission level */
    hasPermission: (route: RouteKey, minLevel: PermissionLevel = 'READ') =>
      role ? hasPermission(role, route, minLevel) : false,

    /** Get all routes this role can access */
    accessibleRoutes: role ? getAccessibleRoutes(role) : [],

    /** Is the user a super admin? */
    isSuperAdmin: role === 'SUPER_ADMIN',
  };
}
